import type { Locale } from '@iac/contracts';
import { useState } from 'react';
import type { Dictionary } from '../i18n/dictionary';
import type { Story } from '../lib/story';
import { Slider } from './Slider';

const sample: Dictionary<{
  length: string;
  size: string;
  sizes: string[];
  wall: string;
  walls: string[];
}> = {
  pl: {
    length: 'Długość',
    size: 'Rozmiar',
    sizes: ['S', 'M', 'L', 'XL'],
    wall: 'Ścianka',
    walls: ['Cienka', 'Gruba'],
  },
  en: {
    length: 'Length',
    size: 'Size',
    sizes: ['S', 'M', 'L', 'XL'],
    wall: 'Wall',
    walls: ['Thin', 'Thick'],
  },
};

/** 25, 50, … 250 — the sizes Boxer's modular grid offers. */
const MILLIMETRES = Array.from({ length: 10 }, (_, i) => 25 + i * 25);

/** The slider owns no state; the screen around it does. */
function Demo<T>({
  id,
  label,
  options,
  initial,
}: {
  id: string;
  label: string;
  options: readonly { value: T; label: string }[];
  initial: T;
}) {
  const [value, setValue] = useState(initial);
  return (
    <Slider
      id={id}
      label={label}
      options={options}
      value={value}
      onChange={setValue}
    />
  );
}

const millimetres = (locale: Locale) => {
  const num = new Intl.NumberFormat(locale);
  return (
    <Demo
      id="story-slider-mm"
      label={sample[locale].length}
      options={MILLIMETRES.map((mm) => ({
        value: mm,
        label: `${num.format(mm)} mm`,
      }))}
      initial={50}
    />
  );
};

const words = (locale: Locale) => (
  <Demo
    id="story-slider-words"
    label={sample[locale].size}
    options={sample[locale].sizes.map((size) => ({ value: size, label: size }))}
    initial="M"
  />
);

const two = (locale: Locale) => (
  <Demo
    id="story-slider-two"
    label={sample[locale].wall}
    options={sample[locale].walls.map((wall, i) => ({ value: i, label: wall }))}
    initial={1}
  />
);

export default {
  kind: 'component',
  title: { pl: 'Suwak', en: 'Slider' },
  description: {
    pl: 'Tor, na którym uchwyt zatrzymuje się tylko na podanych opcjach, każda oznaczona kreską — do wyboru jednej wartości z uporządkowanej listy. Opcje stoją w równych odstępach niezależnie od wartości. Stan i etykiety opcji (z jednostką) trzyma ekran; wybrana opcja jest zawsze widoczna obok etykiety, więc palec jej nie zasłoni. To natywny `range`: przeciąganie, strzałki, Home/End i czytnik ekranu działają od przeglądarki, który słyszy etykietę opcji. Na wąskim ekranie podpisane są tylko skrajne kreski.',
    en: 'A track whose thumb stops only at the given options, each marked with a tick — for picking one value from an ordered list. The options sit at equal distances whatever their values. The screen owns the value and the option labels (units included); the selected option always shows beside the label, so a finger never covers it. It is a native `range`: dragging, the arrow keys, Home/End and screen readers work as the browser provides, and the option label is what gets announced. On a narrow screen only the end ticks are labelled.',
  },
  variants: [
    {
      name: { pl: 'Wymiar w mm', en: 'Size in mm' },
      render: millimetres,
    },
    {
      name: { pl: 'Lista słów', en: 'Words' },
      render: words,
    },
    {
      name: { pl: 'Dwie opcje', en: 'Two options' },
      render: two,
    },
  ],
} satisfies Story;
